/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : Home.jsx
 * description   : {{insert here}}
 * creation date : 13/01/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    useEffect,
    useState,
    Fragment,
} from 'react';

import {
    Text,
    View,
    Image,
    Flex,
} from '@aws-amplify/ui-react';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    id_list,
    hash_list,
} from '@/utils/router/core';

// Pages
import About from '@/pages/Home/About';
import Service from '@/pages/Home/Service';
import Process from '@/pages/Home/Process';
import Clients from '@/pages/Home/Clients';
import Contact from '@/pages/Home/Contact';

// Images
import ImageHomeTitle from '@/assets/images/home/aircraft-right-side.png';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function Home() {
    // Component context
    const { ctx } = useComponentContext();

    // State variables
    const [screenHeight, set_screenHeight] = useState(undefined);

    // Listen asynchronous events: Page load and location change
    useEffect(() => {
        if (
            (ctx.runtime.isLoaded) &&
            (ctx.auth.state !== toolKit.enums.auth.stateTypes.UNMOUNTED)
            && (undefined === screenHeight)
        ) {
            const ref_navbar = toolKit.api.location.serializeHash([id_list.NAVBAR]);

            if (ref_navbar) {
                const obj_navbar = toolKit.api.utils.getBounding(ref_navbar);

                // Compute the screen height
                const val = Math.max(0, (ctx.runtime.screen.inner.height - obj_navbar.height));

                // Set the screen height
                set_screenHeight(val);
            }
        }
    },
        [
            ctx,
            screenHeight,
        ]
    );

    return (
        <Fragment>
            <Flex
                direction={
                    ctx.api.utils.adaptiveOption(
                        'row',
                        'column-reverse',
                        'column-reverse'
                    )
                }
                height={screenHeight}
                gap={ctx.theme.tokens.space.zero}
                justifyContent='center'
                className='main-home-title'
            >
                <Flex
                    width={ctx.theme.tokens.space.relative.full}
                    height={
                        ctx.api.utils.adaptiveOption(
                            ctx.theme.tokens.space.relative.full,
                            '50%',
                            ctx.theme.tokens.space.zero
                        )
                    }
                    justifyContent='center'
                    alignItems={
                        ctx.api.utils.adaptiveOption(
                            'center',
                            'center',
                            'flex-start'
                        )
                    }
                    alignContent='center'
                >
                    <Flex
                        direction='column'
                        gap={ctx.theme.tokens.space.zero}
                        textAlign={
                            ctx.api.utils.adaptiveOption(
                                'left',
                                'center',
                                'center'
                            )
                        }
                        maxWidth={
                            ctx.api.utils.adaptiveOption(
                                '35rem',
                                '35rem',
                                ctx.theme.tokens.space.relative.full
                            )
                        }
                        margin={
                            ctx.api.utils.adaptiveOption(
                                ctx.theme.tokens.space.medium,
                                ctx.theme.tokens.space.medium,
                                ctx.theme.tokens.space.large
                            )
                        }
                        fontSize={
                            ctx.api.utils.adaptiveOption(
                                '2.5rem',
                                '2.2rem',
                                '1.4rem'
                            )
                        }
                        fontWeight={ctx.theme.tokens.fontWeights.medium}
                        lineHeight={ctx.theme.tokens.lineHeights.small}
                        className='main-home-title-text'
                    >
                        <Text
                            fontFamily='Cera Pro'
                            fontSize={
                                ctx.api.utils.adaptiveOption(
                                    '6rem',
                                    '5rem',
                                    '3.2rem'
                                )
                            }
                            fontWeight={ctx.theme.tokens.fontWeights.extrabold}
                            lineHeight={ctx.theme.tokens.lineHeights.small}
                        >
                            {process.env.REACT_APP__PROJECT__NAME.toLocaleUpperCase()}
                        </Text>

                        <Text>
                            {/* TODO: Translate message */}
                            From buildings to airports,
                        </Text>

                        <Text>
                            {/* TODO: Translate message */}
                            we are creating a worldwide
                        </Text>

                        <Text>
                            {/* TODO: Translate message */}
                            3D digital modeling platform.
                        </Text>
                    </Flex>
                </Flex>

                <Flex
                    position='relative'
                    width={ctx.theme.tokens.space.relative.full}
                    height={
                        ctx.api.utils.adaptiveOption(
                            ctx.theme.tokens.space.relative.full,
                            '50%',
                            ctx.theme.tokens.space.zero
                        )
                    }
                    justifyContent='center'
                    alignItems='center'
                    alignContent='center'
                    padding={ctx.theme.tokens.space.medium}
                >
                    <Image
                        src={ImageHomeTitle}
                    />
                </Flex>
            </Flex>

            <View
                className='main-home-content'
            >
                <View
                    className='main-home-content-body'
                >
                    <About
                        id={hash_list.ABOUT}
                    />

                    <Service
                        id={hash_list.SERVICE}
                    />

                    <Process
                        id={hash_list.PROCESS}
                    />

                    <Clients
                        id={hash_list.CLIENTS}
                    />

                    <Contact
                        id={hash_list.CONTACT}
                    />
                </View>
            </View>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export default Home;



// End of file
